/**
 * Admin → Settings (1.4.2): every setting the page shows, in its group, with how it is edited. The plain-
 * English name and the one-sentence purpose of each are i18n keys (`setcat.<code>.name` / `.purpose`, en +
 * es), so the page and docs/ADMIN-GUIDE.md (generated from this list by scripts/gen-settings-guide.ts — a
 * test compares them) cannot drift apart.
 *
 * An entry is a single SM_Settings row (`row`), an app setting from Azure shown read-only and masked
 * (`env`), or a block with its own editor that is not one row (`block`: the day-plan preview, the finish
 * questions, the approved places…). Rows the site has that are not listed here still show, in System,
 * under their own DisplayName; inactive and retired rows go under "Retired settings (n)", read-only.
 *
 * 1.6.9 (D-1.6.9-1): this list is the one source. Every entry carries its key, its title (`group`), its
 * subtitle (optional), its control and the roles that may see and change it; the page (two levels, collapsed),
 * the search, the guide table and the "Change in Settings" links elsewhere in the app are all built from it.
 * No key is listed twice, and tests/settings-catalog-169.test.ts holds the list against the keys the code
 * reads, both ways. The rows of the first setup the app does not read are LEGACY_ROWS, not entries.
 */

import { DEEP_LENGTH_STEPS, GAP_STEPS, VISIT_LENGTH_STEPS, lengthWords } from './dayplan';
import { ARRIVAL_UNDO_KEY, OPEN_TOO_LONG_KEY, OPEN_TOO_LONG_STEPS, UNDO_MINUTE_STEPS } from './press';
import { VOLUNTEER_ORGS_DEFAULT, VOLUNTEER_ORGS_KEY } from './volunteer-affiliation';

/** Mon–Fri, as SharePoint's day choices spell them. */
export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] as const;
/** Where a tech appointment can be held (1.5.0); stored as these words. */
export const TECH_PLACES = ['Boardroom', 'Office', 'Apartment'] as const;
export type TechPlace = (typeof TECH_PLACES)[number];

/** A "checks" value ("Tuesday; Thursday") as its list. */
export function checksOf(value: string | null | undefined): string[] {
  const v = String(value ?? '').trim();
  if (!v || v === 'Not set') return [];
  return v.split(/\s*;\s*/).filter(Boolean);
}

/**
 * 1.6.9 (D-1.6.9-1): the titles of the page, in its order. `group` on an entry is its title (the name is kept
 * from 1.4.2 so the words stay under `setgroup.<title>`).
 */
export const SETTING_GROUPS = ['housekeeping', 'days', 'emails', 'wording', 'recipients', 'techSupport', 'companion', 'events', 'forms', 'system'] as const;
export type SettingGroup = (typeof SETTING_GROUPS)[number];
export const SETTING_TITLES = SETTING_GROUPS;
export type SettingTitle = SettingGroup;

/**
 * The subtitles under each title, in order (their words: `setgroup.<title>.sub.<subtitle>`). A title with none
 * shows its settings as soon as it is opened.
 */
export const SETTING_SUBTITLES: Readonly<Record<SettingGroup, readonly string[]>> = {
  housekeeping: ['timing', 'capacity', 'twoCrews', 'letters'],
  days: [],
  emails: [],
  wording: [],
  recipients: [],
  techSupport: [],
  companion: ['volunteers', 'visions'],
  events: [],
  forms: [],
  system: ['help', 'app', 'health', 'signIn', 'azure'],
};

export type SettingEditor =
  /** free text */
  | 'text'
  /** a number */
  | 'number'
  /** a time of day, hh:mm */
  | 'time'
  /** Yes / No */
  | 'yesno'
  /** several of a short list, stored "A; B" (1.5.0: the tech days, where appointments are held) */
  | 'checks'
  /** Yes / No that stays No (AutomaticEmailEnabled) */
  | 'neverOn'
  /** one of a short list */
  | 'select'
  /** addresses separated by ; (with "Pick from the directory") */
  | 'emails'
  /** the office phones (number + label, a list) */
  | 'phones'
  /** written by the app itself */
  | 'readonly'
  /** an Azure app setting: shown as Set / Not set, never its value */
  | 'secret'
  /** a block with its own editor */
  | 'block';

export interface SettingOption {
  value: string;
  /** i18n key for the option's words, with its parameters */
  label: string;
  params?: Record<string, string | number>;
}

/** The roles of the app, as the catalog names them (shared/roles.ts ROLES — kept as words here so this file has no imports beyond the day plan). */
export type SettingRole = 'admin' | 'coadmin' | 'scheduler' | 'crew' | 'tech' | 'viewer';

export interface SettingDefinition {
  /** The SettingKey (or the app setting's name, or the block's id). */
  code: string;
  /** The title it is under. */
  group: SettingGroup;
  /** The subtitle under that title (one of SETTING_SUBTITLES[group]); none = directly under the title. */
  subtitle?: string;
  editor: SettingEditor;
  source: 'row' | 'env' | 'block';
  options?: SettingOption[];
  /** Saved through /api/settings/email (the email settings' own checks) instead of /api/manage/settings. */
  emailRoute?: boolean;
  /** The value the app uses while the site has no row. */
  fallback?: string;
  /** Who sees it on Admin → Settings. */
  see: readonly SettingRole[];
  /** Who may change it there (nobody: the app writes it, or it lives in Azure). */
  change: readonly SettingRole[];
}

const length = (m: number): SettingOption => ({ value: String(m), label: 'setcat.opt.length', params: { len: lengthWords(m) } });
const minutes = (m: number): SettingOption => ({ value: String(m), label: 'set.minutes', params: { count: m } });
const visits = (n: number): SettingOption => ({ value: String(n), label: 'set.visits', params: { count: n } });
const months = (n: number): SettingOption => ({ value: String(n), label: 'set169.months', params: { count: n } });
const clock = (hhmm: string): SettingOption => ({ value: hhmm, label: 'setcat.opt.clock', params: { time: hhmm } });
const halfHours = (from: number, to: number): SettingOption[] => {
  const out: SettingOption[] = [];
  for (let m = from; m <= to; m += 30) out.push(clock(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`));
  return out;
};
const opt = (value: string, label: string, params?: Record<string, string | number>): SettingOption => ({ value, label, ...(params ? { params } : {}) });

const ADMIN: readonly SettingRole[] = ['admin'];
const NOBODY: readonly SettingRole[] = [];
type Extra = Partial<Pick<SettingDefinition, 'options' | 'emailRoute' | 'fallback'>>;
/** `where` is "title" or "title/subtitle". */
const place = (where: string): { group: SettingGroup; subtitle?: string } => {
  const [group, subtitle] = where.split('/') as [SettingGroup, string | undefined];
  return subtitle ? { group, subtitle } : { group };
};
const row = (code: string, where: string, editor: SettingEditor, extra: Extra = {}): SettingDefinition => ({
  code,
  ...place(where),
  editor,
  source: 'row',
  ...extra,
  see: ADMIN,
  change: editor === 'readonly' || editor === 'neverOn' ? NOBODY : ADMIN,
});
const block = (code: string, where: string, change: readonly SettingRole[] = ADMIN): SettingDefinition => ({ code, ...place(where), editor: 'block', source: 'block', see: ADMIN, change });
const env = (code: string, where: string): SettingDefinition => ({ code, ...place(where), editor: 'secret', source: 'env', see: ADMIN, change: NOBODY });

/** The page's order: titles in SETTING_GROUPS order, subtitles in SETTING_SUBTITLES order, entries in this order within each. */
export const SETTINGS_CATALOG: SettingDefinition[] = [
  // ── Housekeeping → Visit timing
  block('dayplan', 'housekeeping/timing', NOBODY),
  row('DAY_PLAN_START', 'housekeeping/timing', 'select', { options: halfHours(8 * 60, 11 * 60), fallback: '09:30' }),
  row('DAY_PLAN_VISIT_MINUTES', 'housekeeping/timing', 'select', { options: VISIT_LENGTH_STEPS.map(length), fallback: '60' }),
  row('DAY_PLAN_LUNCH_START', 'housekeeping/timing', 'select', { options: halfHours(11 * 60 + 30, 13 * 60), fallback: '12:00' }),
  row('DAY_PLAN_LUNCH_MINUTES', 'housekeeping/timing', 'select', { options: [30, 60].map(minutes), fallback: '60' }),
  row('DAY_PLAN_END', 'housekeeping/timing', 'select', { options: halfHours(15 * 60, 17 * 60), fallback: '16:00' }),
  row('DAY_PLAN_OVERRIDE_TODAY', 'housekeeping/timing', 'select', { options: [opt('', 'set.dpNone'), ...['08:00', '08:30', '09:00'].map((v) => opt(v, 'set.dpStartToday', { time: v }))], fallback: '' }),
  // 1.5.2: the arrival windows tenants are told (DAY_PLAN_WINDOWS), with the last 30 days' observed times beside them
  block('DAY_PLAN_WINDOWS', 'housekeeping/timing'),
  row('HK_DEEP_LENGTH', 'housekeeping/timing', 'select', { options: DEEP_LENGTH_STEPS.map(length), fallback: '180' }),
  // 1.6.4: a staff unit's visit when nobody chose a length
  row('HK_STAFF_LENGTH', 'housekeeping/timing', 'select', { options: VISIT_LENGTH_STEPS.map(length), fallback: '90' }),
  // 1.7.8: an office's visit when nobody chose a length ("offices are 30 minutes, sometimes an hour")
  row('HK_OFFICE_LENGTH', 'housekeeping/timing', 'select', { options: VISIT_LENGTH_STEPS.map(length), fallback: '30' }),
  row('HK_GAP_MINUTES', 'housekeeping/timing', 'select', { options: GAP_STEPS.map(minutes), fallback: '10' }),
  // 1.6.5: Undo after Arrived / Mark completed, and when an open visit is asked about
  row(ARRIVAL_UNDO_KEY, 'housekeeping/timing', 'select', { options: UNDO_MINUTE_STEPS.map(minutes), fallback: '10' }),
  row(OPEN_TOO_LONG_KEY, 'housekeeping/timing', 'select', { options: OPEN_TOO_LONG_STEPS.map(minutes), fallback: '30' }),
  // ── Housekeeping → Days and capacity
  row('HK_WARN_AT', 'housekeeping/capacity', 'select', { options: [5, 6, 7].map(visits), fallback: '6' }),
  row('HK_WARN_AT_DEEP_DAY', 'housekeeping/capacity', 'select', { options: [4, 5].map(visits), fallback: '5' }),
  row('HK_PICK_SPOT_ENABLED', 'housekeeping/capacity', 'yesno', { fallback: 'No' }),
  // 1 Nov 2026 (D-1102-CREW): the crew's phone
  row('HK_TODAY_LIVE_REFRESH', 'housekeeping/capacity', 'yesno', { fallback: 'Yes' }),
  // 3 Nov 2026 (D-1103-SIMPLE): fewer places to look
  row('HK_SIMPLER_SCREENS', 'system/app', 'yesno', { fallback: 'Yes' }),
  // 3 Nov 2026 (D-1103-VB): finishing the volunteer booking redesign
  row('VOL_OUTING_WITHDRAW', 'companion/volunteers', 'yesno', { fallback: 'Yes' }),
  row('HK_CREW_OFFLINE', 'housekeeping/capacity', 'yesno', { fallback: 'No' }),
  // 4 Nov 2026 (D-1104-NEEDS): Planning's Still needs a date, and the 15th line on Today
  row('HK_NEEDS_BOOK', 'housekeeping/capacity', 'yesno', { fallback: 'Yes' }),
  row('HK_NOT_SEEN_FIRST', 'housekeeping/capacity', 'yesno', { fallback: 'Yes' }),
  row('HK_MISSED_BACK_ON_LIST', 'housekeeping/capacity', 'yesno', { fallback: 'Yes' }),
  row('HK_USUAL_DAY_FIRST', 'housekeeping/capacity', 'yesno', { fallback: 'Yes' }),
  row('HK_SECOND_VISIT_NUDGE', 'housekeeping/capacity', 'yesno', { fallback: 'Yes' }),
  // 4 Nov 2026 (D-1104-FIT): the plain Does the month fit?
  row('HK_PLAN_FIT_PLAIN', 'housekeeping/capacity', 'yesno', { fallback: 'Yes' }),
  // 4 Nov 2026 (D-1104-BOOK): Book asks whether the tenant confirmed the date, and offers the second visit
  row('HK_BOOK_ASK_CONFIRM', 'housekeeping/capacity', 'yesno', { fallback: 'Yes' }),
  row('HK_BOOK_SECOND_VISIT', 'housekeeping/capacity', 'yesno', { fallback: 'Yes' }),
  // 1.5.15: the apartments that are staff units or offices — a visit spot, never a tenant
  row('HK_STAFF_UNITS', 'housekeeping/capacity', 'text', { fallback: '1205; 1207; 1208; 1209 office' }),
  // 1102: the evening and early-morning day sheet (a large-print PDF in the library)
  row('DAY_SHEET_SAVE', 'housekeeping/capacity', 'yesno', { fallback: 'Yes' }),
  // ── Housekeeping → Two crews (1.6.6): set on a day, not here — a sentence and a link
  block('twoCrews', 'housekeeping/twoCrews', NOBODY),
  // ── Housekeeping → Week letters
  row('HK_WEEK_LETTER_MODE', 'housekeeping/letters', 'select', { options: [opt('rolling', 'setcat.opt.rolling'), opt('calendar', 'setcat.opt.calendar')], fallback: 'calendar' }),
  row('HK_WEEK_LETTER_ANCHOR', 'housekeeping/letters', 'text', { fallback: 'Not set' }),

  // ── Closed days and service days (1.6.9)
  block('closedDays', 'days'),
  block('serviceDays', 'days', NOBODY),
  row('HK_DAYS_AHEAD', 'days', 'select', { options: [1, 2, 3, 4, 5, 6].map(months), fallback: '3' }),
  row('HK_DAYS_AUTO_READY', 'days', 'yesno', { fallback: 'Yes' }),

  // ── Emails and timing
  row('EMAIL_SEND_DELAY_MINUTES', 'emails', 'select', {
    emailRoute: true,
    options: [...[2, 5, 10, 15, 30, 60].map((m) => opt(String(m), 'emailSettings.minutes', { count: m })), opt('NEXT_MORNING', 'emailSettings.nextMorning')],
    fallback: '5',
  }),
  row('EMAIL_ON_PHONE_BOOKING', 'emails', 'yesno', { emailRoute: true, fallback: 'Yes' }),
  row('EMAIL_ON_CONFIRMATION', 'emails', 'yesno', { emailRoute: true, fallback: 'Yes' }),
  // D-1104-MBX-D-3: failed emails on Today (admin and co-admin only). No = Today exactly as before.
  row('EMAIL_FAILED_ON_TODAY', 'emails', 'yesno', { fallback: 'Yes' }),
  row('EMAIL_REMINDER_RULE', 'emails', 'select', {
    emailRoute: true,
    options: [opt('DAY_BEFORE_0900', 'emailSettings.dayBefore9'), opt('DAY_BEFORE_1600', 'emailSettings.dayBefore4'), opt('TWO_DAYS_BEFORE', 'emailSettings.twoDays'), opt('OFF', 'emailSettings.remOff')],
    fallback: 'DAY_BEFORE_0900',
  }),
  row('TENANT_RESCHEDULE_CUTOFF_HOURS', 'emails', 'select', { emailRoute: true, options: [opt('24', 'emailSettings.h24'), opt('48', 'emailSettings.h48'), opt('SAME_DAY_0800', 'emailSettings.sameDay8')], fallback: '24' }),
  row('EMAIL_READ_REPLIES', 'emails', 'yesno', { emailRoute: true, fallback: 'No' }),
  row('EMAIL_CLOSEOUT_REMINDER_MINUTES', 'emails', 'select', { emailRoute: true, options: [...[30, 60, 90, 120].map((m) => opt(String(m), 'emailSettings.minutes', { count: m })), opt('OFF', 'emailSettings.remOff')], fallback: '60' }),
  row('AutomaticEmailEnabled', 'emails', 'neverOn', { fallback: 'No' }),

  // ── Email wording: every template, grouped by who receives it — the one place a template is found (1.6.9)
  block('wording', 'wording'),

  // ── Who gets what: the only place an address or a phone lives (1.6.9)
  row('OFFICE_PHONE', 'recipients', 'phones', { emailRoute: true }),
  row('OFFICE_EMAIL', 'recipients', 'emails', { emailRoute: true }),
  // 4 Nov 2026 (D-1104-FWD-1): a copy of every real reply to the mailbox goes to these staff addresses; empty = none
  row('REPLY_FORWARD_TO', 'recipients', 'emails', { emailRoute: true }),
  row('BUILDING_MANAGER_NAME', 'recipients', 'text', { emailRoute: true }),
  row('BUILDING_MANAGER_EMAIL', 'recipients', 'emails', { emailRoute: true }),
  // 1.5.17: who gets the record of a permission to enter given by phone (Not set = the building manager)
  row('CYNTHIA_EMAIL', 'recipients', 'emails', { emailRoute: true }),
  // 1102: who gets the email when Health turns red (Not set = the Director's address, "Group emails: To")
  row('HEALTH_EMAIL_TO', 'recipients', 'emails', { emailRoute: true }),
  // 1.6.9: the tech ticket's address moved here from Tech Support
  row('TECH_TICKET_EMAIL', 'recipients', 'emails', { fallback: 'Not set' }),
  row('VISIONS_LIAISON_EMAIL', 'recipients', 'emails'),
  row('EMAIL_SAFETY_RECIPIENTS', 'recipients', 'emails', { emailRoute: true }),
  row('EMAIL_INCIDENT_RECIPIENTS', 'recipients', 'emails', { emailRoute: true }),
  row('EMAIL_ENTRY_PERMISSION_RECIPIENTS', 'recipients', 'emails', { emailRoute: true }),
  row('EMAIL_INTERNAL_RECIPIENTS', 'recipients', 'emails', { emailRoute: true }),
  row('MAIL_GROUP_TO', 'recipients', 'emails', { emailRoute: true }),
  row('MAIL_GROUP_CC', 'recipients', 'emails', { emailRoute: true }),

  // ── Tech Support (1.5.0)
  block('techProgram', 'techSupport', NOBODY),
  row('TECH_DAYS', 'techSupport', 'checks', { options: WEEKDAYS.map((d) => opt(d, `weekday.${d}`)), fallback: 'Wednesday' }),
  row('TECH_START', 'techSupport', 'select', { options: halfHours(8 * 60, 15 * 60), fallback: '10:00' }),
  row('TECH_END', 'techSupport', 'select', { options: halfHours(11 * 60, 18 * 60), fallback: '15:00' }),
  row('TECH_PLACES', 'techSupport', 'checks', { options: TECH_PLACES.map((x) => opt(x, `tech.place.${x}`)), fallback: TECH_PLACES.join('; ') }),
  row('TECH_LENGTH_MINUTES', 'techSupport', 'select', { options: [30, 45, 60, 90].map(minutes), fallback: '45' }),
  // 1.8.5 (D-1.8.5-3): the ticket email has a switch here, and the tech has his own in My settings — either off, no email
  row('TECH_TICKET_EMAIL_ON', 'techSupport', 'yesno', { fallback: 'Yes' }),
  // 1.5.3: empty until counsel approves a Spanish text of the agreement
  row('TECH_RELEASE_TEXT_ES', 'techSupport', 'text', { fallback: 'Not set' }),
  // 1.8.5 (D-1.8.5-0b, D-1.8.5-10): the Tech Wizard — the heads-up switch, the four prices per million tokens and
  // the two thresholds of the monthly cap (the fallbacks are shared/tech-wizard.ts TECH_AI_DEFAULTS; a test holds them equal)
  row('TECH_WIZARD_READS_TICKETS', 'techSupport', 'yesno', { fallback: 'Yes' }),
  row('TECH_WIZARD_WEB_SEARCH', 'techSupport', 'yesno', { fallback: 'No' }),
  // D-1025-WIZ-1: which answer engine the Wizard uses; Checked unless this says Classic
  row('TECH_WIZARD_ENGINE', 'techSupport', 'select', { options: [opt('Checked (new)', 'set.wizEngineChecked'), opt('Classic (before 25 Oct)', 'set.wizEngineClassic')], fallback: 'Checked (new)' }),
  row('TECH_AI_LUNA_INPUT_PER_M', 'techSupport', 'number', { fallback: '0.22' }),
  row('TECH_AI_LUNA_OUTPUT_PER_M', 'techSupport', 'number', { fallback: '1.32' }),
  row('TECH_AI_TERRA_INPUT_PER_M', 'techSupport', 'number', { fallback: '2.20' }),
  row('TECH_AI_TERRA_OUTPUT_PER_M', 'techSupport', 'number', { fallback: '13.20' }),
  row('TECH_AI_FIRST_ONLY_AT', 'techSupport', 'number', { fallback: '10' }),
  row('TECH_AI_PAUSE_AT', 'techSupport', 'number', { fallback: '15' }),

  // ── Companion & Wellness → Volunteers
  row('COMPANION_TRAINING_DATE', 'companion/volunteers', 'text', { emailRoute: true }),
  row('COMPANION_TRAINING_TIME', 'companion/volunteers', 'text', { emailRoute: true }),
  row('COMPANION_TRAINER_NAME', 'companion/volunteers', 'text', { emailRoute: true }),
  // 1.7.2: a Companion request is schedule-first; the widen press is offered after this many days with no answer
  row('COMPANION_WIDEN_DAYS', 'companion/volunteers', 'select', { emailRoute: true, options: [opt('0', 'setcat.opt.widenNow'), ...[1, 2, 3, 5, 7].map((d) => opt(String(d), 'setcat.opt.widenDays', { count: d }))], fallback: '2' }),
  // 1.7.9: where a volunteer comes from — the Affiliation dropdown's list (Other is always offered)
  row(VOLUNTEER_ORGS_KEY, 'companion/volunteers', 'text', { fallback: VOLUNTEER_ORGS_DEFAULT }),
  // 3 Nov 2026 (D-1103-PAIR): the tenant's regular volunteer on Book
  row('VOL_USUAL_PARTNER', 'companion/volunteers', 'yesno', { fallback: 'Yes' }),
  // 1.8.3: the services the office adds ("Piano") beside the six built-in — add and retire, never remove
  block('volunteerServices', 'companion/volunteers'),
  block('places', 'companion/volunteers'),
  // ── Companion & Wellness → VISIONS (the liaison's address is under Who gets what)
  block('visionsRule', 'companion/visions', NOBODY),
  block('visionsCodes', 'companion/visions'),
  block('visionsTemplate', 'companion/visions'),

  // ── Events and RSVPs (their emails are under Email wording)
  block('eventRules', 'events', NOBODY),
  // 1.7.4: what a new event starts with; each event keeps its own number (the event's page, The numbers)
  row('EVENT_GUESTS_ALLOWED', 'events', 'select', { emailRoute: true, options: [opt('0', 'setcat.opt.guests0'), opt('1', 'setcat.opt.guests1'), opt('2', 'setcat.opt.guests2')], fallback: '2' }),
  // 1.7.7: the calendar's two lists, and the reminders (on / off — the kill switch —, when, to whom)
  row('EVENT_KINDS', 'events', 'text', { emailRoute: true, fallback: 'Bingo; Music; Movie night; Tech discussion; Nail spa; Outing; Meeting; Other' }),
  row('EVENT_ROOMS', 'events', 'text', { emailRoute: true, fallback: 'Auditorium; Boardroom; Lobby; Community room' }),
  block('eventReminders', 'events'),
  row('EVENT_REMINDERS', 'events', 'yesno', { emailRoute: true, fallback: 'Yes' }),
  block('EVENT_REMINDER_TIMES', 'events'),
  row('EVENT_REMINDER_AUDIENCE', 'events', 'select', { emailRoute: true, options: [opt('ALL', 'setcat.opt.remindAll'), opt('YES', 'setcat.opt.remindYes'), opt('NOBODY', 'setcat.opt.remindNobody')], fallback: 'ALL' }),
  row('EVENT_DESCRIPTION_ADDED', 'events', 'yesno', { emailRoute: true, fallback: 'Yes' }),

  // ── Forms and questions
  // D-1103-ENROLL-5: tenants can complete an enrollment from an emailed link (People). Only adds buttons; No = today exactly.
  row('TENANT_SELF_ENROLL', 'forms', 'yesno', { fallback: 'Yes' }),
  block('finishQuestions', 'forms'),
  block('deepMeans', 'forms'),
  // 1.5.11: the home visit checklist's questions
  block('homeVisitQuestions', 'forms'),
  // 1.5.17: the terms staff read aloud when a tenant gives permission to enter by phone (shared/office-action.ts)
  block('permissionTerms', 'forms'),

  // ── System → Help videos (1.4.2; 1.5.11; 1.5.16): a SharePoint / Stream link or YouTube
  row('HELP_VIDEO_URL_EN', 'system/help', 'text', { fallback: 'Not set' }),
  row('HELP_VIDEO_URL_ES', 'system/help', 'text', { fallback: 'Not set' }),
  row('HELP_VIDEO_START_EN', 'system/help', 'text', { fallback: 'Not set' }),
  row('HELP_VIDEO_START_ES', 'system/help', 'text', { fallback: 'Not set' }),
  row('HELP_VIDEO_SIGNUP_EN', 'system/help', 'text', { fallback: 'Not set' }),
  row('HELP_VIDEO_SIGNUP_ES', 'system/help', 'text', { fallback: 'Not set' }),
  row('HELP_VIDEO_BOOKING_EN', 'system/help', 'text', { fallback: 'Not set' }),
  row('HELP_VIDEO_BOOKING_ES', 'system/help', 'text', { fallback: 'Not set' }),
  row('HELP_VIDEO_PLANNING_EN', 'system/help', 'text', { fallback: 'Not set' }),
  row('HELP_VIDEO_PLANNING_ES', 'system/help', 'text', { fallback: 'Not set' }),
  // ── System → The app
  // 1.5.6: the app's address — every link the app writes down starts with it
  row('APP_BASE_URL', 'system/app', 'text', { fallback: 'https://scheduler.abfselis.org' }),
  row('ASSISTANT_ENABLED', 'system/app', 'yesno', { fallback: 'Yes' }),
  row('ASSISTANT_DATA_ENABLED', 'system/app', 'yesno', { fallback: 'Yes' }),
  row('ANNOUNCE_DRAFT_ASSISTANT', 'system/app', 'yesno', { fallback: 'Yes' }),
  // 1102: the public pages keep junk out (hidden field, limits, size caps); Health turning red emails the Director
  row('PUBLIC_PAGES_GUARD', 'system/app', 'yesno', { fallback: 'Yes' }),
  row('HEALTH_EMAIL_ON_RED', 'system/app', 'yesno', { fallback: 'Yes' }),

  // 1 Nov 2026 (D-1102-OFFICE-1): the "Might slip — check these" list on Today
  row('MIGHT_SLIP_ENABLED', 'system/app', 'yesno', { fallback: 'Yes' }),
  // 2 Nov 2026 night (D-1103-SPEED-2): the last picture of a screen, shown while it updates
  row('UI_KEEP_LAST_PICTURE', 'system/app', 'yesno', { fallback: 'Yes' }),

  // 3 Nov 2026 (D-1103-NEXT): "Do next" on Today (an HK_ key so the page reads it with the day plan settings)
  row('HK_TODAY_DO_NEXT', 'system/app', 'yesno', { fallback: 'Yes' }),
  // 1.5.7: with it on, "Call through Teams" is a choice beside "Hide my number" (shared/calling.ts)
  row('TEAMS_PHONE_ENABLED', 'system/app', 'yesno', { fallback: 'No' }),
  // 3 Nov 2026 (D-1103-FORM): upload a Word form and make it signable (Admin only; off until the Director has run the live test)
  row('FORMS_UPLOAD_ENABLED', 'system/app', 'yesno', { fallback: 'No' }),
  block('programRequirements', 'system/app', NOBODY),
  // ── System → Health stamps
  row('EMAIL_DISPATCH_LAST_RUN', 'system/health', 'readonly'),
  row('ENGINE_LAST_RUN', 'system/health', 'readonly'),
  // ── System → Sign-in (Azure app settings, shown Set / Not set)
  env('AAD_CLIENT_ID', 'system/signIn'),
  env('AAD_CLIENT_SECRET', 'system/signIn'),
  env('ALLOWED_EMAIL_DOMAINS', 'system/signIn'),
  // ── System → Azure settings
  env('AOAI_ENDPOINT', 'system/azure'),
  env('AOAI_KEY', 'system/azure'),
  env('AOAI_DEPLOYMENT', 'system/azure'),
  // 1.8.5 (D-1.8.5-10): the Tech Wizard's two deployments (empty → AOAI_DEPLOYMENT)
  env('AOAI_TECH_DEPLOYMENT_FIRST', 'system/azure'),
  env('AOAI_TECH_DEPLOYMENT_STRONG', 'system/azure'),
  env('TRANSLATOR_KEY', 'system/azure'),
  env('TRANSLATOR_ENDPOINT', 'system/azure'),
  env('TRANSLATOR_REGION', 'system/azure'),
  env('AIRTABLE_TOKEN', 'system/azure'),
];

/**
 * Rows kept from the site's first setup that the app does not read (1.6.9: out of the catalog proper). They are
 * shown read-only under System → "Retired settings", with RETIRED_ROWS and any row switched off on the site.
 * Their words stay under `setcat.<key>`.
 */
export const LEGACY_ROWS = [
  'HousekeepingStandardCapacity',
  'HousekeepingAdditionalCapacity',
  'HousekeepingStandardCrewSize',
  'HousekeepingSurgeApprovalRequired',
  'HousekeepingCapacityWarningPercent',
  'HousekeepingMonthlyReserveTarget',
  'HousekeepingDayStart',
  'HousekeepingLatestEnd',
  'NoShowCountsAgainstAuthorization',
  'ProposalGenerationDay',
  'ProposalReviewDeadlineDays',
  'CurrentHousekeepingWaiverVersion',
  'ReminderHoursBefore',
  'NotificationSenderDisplayName',
  'DefaultCommunicationRetryCount',
  'CommunicationRetryDelayMinutes',
  'UrgentCommunicationRetryCount',
  'ReadingDefaultMinutes',
  'OrganizationDisplayName',
  'OrganizationTimeZone',
  'WeekStartDay',
  'ApplicationVersion',
] as const;

/** The entries of one title (and, when given, one subtitle; `null` = the ones directly under the title). */
export function entriesOf(group: SettingGroup, subtitle?: string | null): SettingDefinition[] {
  return SETTINGS_CATALOG.filter((d) => d.group === group && (subtitle === undefined || (d.subtitle ?? null) === subtitle));
}

/** Where a setting is on the page: `/admin/settings?open=<code>` opens its title and subtitle and marks its row. */
export function settingsLink(code: string): string {
  return `/admin/settings?open=${encodeURIComponent(code)}`;
}

/* ---------------- Email wording (1.6.9): every template under one recipient ---------------- */

/** Who receives it, from the site's RecipientType ("Tenant", "Volunteer", "Internal Staff", anything else). */
export const WORDING_GROUPS = ['tenant', 'volunteer', 'staff', 'other'] as const;
export type WordingGroup = (typeof WORDING_GROUPS)[number];
export function wordingGroupOf(recipientType: string): WordingGroup {
  if (/tenant/i.test(recipientType)) return 'tenant';
  if (/volunteer/i.test(recipientType)) return 'volunteer';
  if (/staff|internal/i.test(recipientType)) return 'staff';
  return 'other';
}

/** Each template under exactly one group, in the list's own order. */
export function groupTemplates<T extends { recipientType: string }>(templates: readonly T[]): Record<WordingGroup, T[]> {
  const out: Record<WordingGroup, T[]> = { tenant: [], volunteer: [], staff: [], other: [] };
  for (const x of templates) out[wordingGroupOf(x.recipientType)].push(x);
  return out;
}

/** Rows kept on the site but no longer read (never deleted): shown read-only under "Retired settings". */
export const RETIRED_ROWS: Readonly<Record<string, string>> = {
  OfficePhone: 'OFFICE_PHONE',
  COMPANION_NEXT_TRAINING_DATE: 'COMPANION_TRAINING_DATE',
  COMPANION_TRAINER: 'COMPANION_TRAINER_NAME',
};

/** The app settings (Azure) the page may report as set / not set — never their values. */
export const ENV_SETTINGS = SETTINGS_CATALOG.filter((d) => d.source === 'env').map((d) => d.code);

/** The one-day early start is stored as DAY_PLAN_OVERRIDE_yyyyMMdd; the page shows today's as one entry. */
export const DAY_PLAN_OVERRIDE_TODAY = 'DAY_PLAN_OVERRIDE_TODAY';

export function catalogEntry(code: string): SettingDefinition | undefined {
  return SETTINGS_CATALOG.find((d) => d.code === code);
}

/** Folds case and accents for the search ("Sí" finds "si"). */
export function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** True when every word of the query is in one of the texts (name, purpose, code, current value). */
export function matchesSearch(query: string, texts: readonly string[]): boolean {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = texts.map(fold).join('\n');
  return words.every((w) => hay.includes(w));
}

/** Splits a text into plain and matched parts for the highlight. */
export function highlightParts(text: string, query: string): { text: string; hit: boolean }[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (!words.length || !text) return [{ text, hit: false }];
  const f = fold(text);
  // folding keeps one character per character for the scripts used here, so positions line up
  if (f.length !== text.length) return [{ text, hit: false }];
  const marks = new Array<boolean>(text.length).fill(false);
  for (const w of words) {
    let i = f.indexOf(w);
    while (i >= 0) {
      for (let j = i; j < i + w.length; j++) marks[j] = true;
      i = f.indexOf(w, i + w.length);
    }
  }
  const out: { text: string; hit: boolean }[] = [];
  for (let i = 0; i < text.length; i++) {
    const last = out[out.length - 1];
    if (last && last.hit === marks[i]) last.text += text[i];
    else out.push({ text: text[i]!, hit: marks[i]! });
  }
  return out;
}

/** What a block shows as its code: where the setting lives. */
export const BLOCK_CODES: Readonly<Record<string, string>> = {
  dayplan: 'DAY_PLAN_*',
  deepMeans: '_app/finish-questions.json · deep',
  finishQuestions: '_app/finish-questions.json',
  homeVisitQuestions: '_app/home-visit-questions.json',
  permissionTerms: '_app/settings/permission-terms.json',
  wording: 'SM_EmailTemplates',
  places: '_app/settings/approved-places.json',
  // 1.8.3
  volunteerServices: '_app/settings/volunteer-services.json',
  eventRules: 'SM_Events',
  visionsRule: 'Exhibit A',
  visionsCodes: '_app/settings/visions-codes.json',
  visionsTemplate: '_app/settings/visions-exhibit-a-template.xlsx',
  programRequirements: 'DESIGN-DECISIONS-v4 (read-only)',
  closedDays: 'SM_BlackoutDates · _app/settings/closed-days.json',
  techProgram: 'SM_Programs · TECH',
  // 1.6.9
  serviceDays: 'Admin → Service days',
  twoCrews: '_app/day-crews/yyyyMMdd.json',
};

/** The code shown small under a setting's name. */
export function shownCode(d: SettingDefinition): string {
  if (d.source === 'block') return BLOCK_CODES[d.code] ?? d.code;
  if (d.code === DAY_PLAN_OVERRIDE_TODAY) return 'DAY_PLAN_OVERRIDE_yyyyMMdd';
  return d.code;
}
